/**
 * The data model. Everything the tournament is, as plain data.
 *
 * The state is never edited directly: it is the fold of an append-only event log (see
 * state.ts). That gives the audit log for free, undo as a correcting event, recovery after
 * any reload, and a simulation that is just a list of events fed in.
 */

export type PlayerId = string;

/** expected = on the list, not here yet. in = here and available. break = here, skip me. left = gone. */
export type PlayerStatus = "expected" | "in" | "break" | "left";

export type Mode = "americano" | "mexicano";

/** idle = between rounds. playing = the game clock runs. changeover = the 2 minutes to swap. */
export type Phase = "idle" | "playing" | "changeover";

/** Which screen a name is being shown on (see displayName in selectors.ts). */
export type Where = "wall" | "phone" | "tablet" | "admin";

export interface Player {
  id: PlayerId;
  name: string;
  status: PlayerStatus;
  /** Catch-up games a latecomer may still claim (see scheduler.ts). */
  catchUpCredits: number;
  /** What they call themselves on the wall. Null until they set one. */
  nickname: string | null;
  /** The version of their picture (the picture itself is kept beside the log). 0 = none yet. */
  photo: number;
  /** An admin let them in without a picture. */
  photoWaived: boolean;
  /** A name a winner gave them after a thrashing; shown on the wall until round `untilRound` is over. */
  dub: { nickname: string; by: PlayerId; untilRound: number } | null;
  /** Curses they may still throw. */
  cursesLeft: number;
  /** When they last heckled, for the limit. */
  lastHeckleAt: number | null;
  /** How many heckles they have sent all day, for the award. */
  heckles: number;
}

/** The numbers behind the bench (decisions 28 and 33). */
export interface BenchRules {
  /** Fun points everyone starts with. Nothing can be lost any more, so none. */
  startingFun: number;
  heckleEveryMs: number;
  heckleMaxChars: number;
  cursesEach: number;
  /** How long a curse lasts, whichever it is. */
  curseMs: number;
  /** What beating the 1st, 2nd and 3rd on the main board is worth to each winner. */
  bounties: number[];
  /** The first round that carries bounties. */
  bountyFromRound: number;
  /** From this many right picks in a row, a right pick is worth 2. */
  pickStreakFrom: number;
  /** Win by this many and a winner may rename a loser. */
  dubMargin: number;
  /** For how many rounds the new name shows. */
  dubRounds: number;
  nicknameMax: number;
}

export interface Heckle {
  /** The sequence number of the event that sent it. */
  seq: number;
  by: PlayerId;
  /** The court it is aimed at, or null for the hall. */
  court: number | null;
  text: string;
  at: number;
}

/** The curses a thrower picks from: wrong handed, no smashes, or one of the pair plays alone. */
export type CurseKind = "wrong-hand" | "no-smashes" | "solo";

/** A curse: one pair plays under it from startsAt to endsAt. Kept all day, for the award. */
export interface Curse {
  by: PlayerId;
  roundNo: number;
  court: number;
  side: "a" | "b";
  startsAt: number;
  endsAt: number;
  /** Which curse. Absent on one thrown before there was a choice: that was wrong hand. */
  kind?: CurseKind;
  /** Solo only: the one of the pair who steps off, drawn by the reducer from the seed. */
  stepsOff?: PlayerId;
}

/** A pick: who a resting player says wins a court. Free; a right one is a fun point. */
export interface Pick {
  by: PlayerId;
  roundNo: number;
  court: number;
  /** The pair they backed. A pick made on a preview only counts if that pair plays that court. */
  pair: Pair;
}

export interface Bench {
  /** Heckles off (an admin's switch). */
  muted: boolean;
  /**
   * Curses off for now (an admin's switch, the `bench.curses` event). Absent in a state made
   * before the switch existed: read it as on, so an old log replays as it was played.
   */
  cursesOff?: boolean;
  /** The most recent heckles, oldest first. */
  heckles: Heckle[];
  curses: Curse[];
  picks: Pick[];
}

/** A handicap the whole hall plays under for a short while in the middle of a round. */
export interface Twist {
  eventId: string;
  startsAt: number;
  endsAt: number;
}

/**
 * A mini game, always for the whole hall (decision 33). A scoring one changes how the whole game
 * is scored on every court; a handicap is a timed twist in the middle of the round.
 */
export interface EventType {
  id: string;
  name: string;
  kind: "scoring" | "handicap";
  /** What a game under this event is played to (first-to-7 sets 7). Absent = the config target. */
  target?: number;
  /** Whether win-by-2 applies (the golden point sets false). Absent = the config rule. */
  winBy2?: boolean;
  /** Multiplier on the team's points for the MAIN board, only if countsOnMain (double points: 2). */
  mainMultiplier: number;
  /** Multiplier on the team's points for the FUN board (double points: 1, so the extra lands there). */
  funMultiplier: number;
  /** Bonus to each winner: always on the fun board; on the main board only if countsOnMain. */
  winBonus: number;
  /** Whether the multiplier and bonus also count on the main leaderboard. */
  countsOnMain: boolean;
}

/** The look chosen on the wall: which ground the screens use and which colour announces things. */
export type Palette = "white" | "cream" | "yellow" | "cards-on-dark";
export type Accent = "pink" | "cyan" | "ink";
/** The wall's lettering: three as written, or the old condensed capitals. */
export type Lettering = "grotesque" | "round" | "condensed" | "caps";
export interface Look {
  palette: Palette;
  accent: Accent;
  /** Absent on a look saved before the lettering was a choice: the grotesque. */
  lettering?: Lettering;
}

export interface Config {
  courts: number;
  playersPerCourt: 4;
  gameMs: number;
  changeoverMs: number;
  /** Points to win a game. */
  target: number;
  winBy2: boolean;
  /** Games needed to be eligible for the podium. */
  minGamesForPodium: number;
  /** Most catch-up games a latecomer can be granted. */
  catchUpMax: number;
  /**
   * Someone kept off court for the day's first rounds, by real name (Abhi, 17 Oct 2026: he stays
   * off for round 1 to make sure the tech is working). They are drawn as sitting out, so it counts
   * as an ordinary sit and they go on first after it. Null or absent: nobody.
   */
  sitsOutFirst?: { name: string; rounds: number } | null;
  /**
   * When on, the changeover rolls straight into the next round. Off by default since decision 41:
   * an admin starts each round once the courts are ready. A tournament made before that, whose
   * settings never named it, keeps starting rounds by itself (see tournament.created in state.ts).
   */
  autoAdvance: boolean;
  /** The last whistle, ms since the epoch: no round starts by itself if its game would end after it. */
  lastWhistleAt: number | null;
  /** When on, each round has randomEventChance of an event being fired by itself. */
  randomEvents: boolean;
  randomEventChance: number;
  /** How long a twist lasts. */
  twistMs: number;
  eventTypes: EventType[];
  /** Set from the calibration screen on the day; every screen follows it. */
  look: Look;
  bench: BenchRules;
}

export type Pair = [PlayerId, PlayerId];

export interface CourtGame {
  /** 1-based court number. */
  court: number;
  a: Pair;
  b: Pair;
  /** The event applied to this court this round, if any. */
  eventId: string | null;
}

/** A round's line-up. Everyone playing or sitting out was "in" when it was drawn. */
export interface Lineup {
  mode: Mode;
  courts: CourtGame[];
  sittingOut: PlayerId[];
  /** Latecomers playing two rounds running on a catch-up credit (spent when the round starts). */
  catchUp: PlayerId[];
}

export interface Score {
  a: number;
  b: number;
  /** True when the clock ended the game (the leader wins; level means a sudden-death point). */
  capped: boolean;
}

export interface ScoreEntry {
  score: Score;
  by: string;
  at: number;
  reason?: string;
}

export interface GameRecord {
  court: number;
  a: Pair;
  b: Pair;
  eventId: string | null;
  /** The score that counts, or null while it is still owed. */
  score: Score | null;
  voided: boolean;
  voidReason: string | null;
  /** Every score ever entered for this game, oldest first. The audit trail. */
  history: ScoreEntry[];
  /** Who cursed this game, if anyone. */
  cursedBy: PlayerId | null;
  /** The winners have used their naming right from this game. */
  dubUsed: boolean;
}

export interface Bounty {
  player: PlayerId;
  /** Fun points to each opponent if this player's pair loses. */
  value: number;
}

export interface Round {
  /** 1-based. */
  no: number;
  mode: Mode;
  startedAt: number;
  lineup: Lineup;
  games: GameRecord[];
  /** Events announced for the whole round (every court). */
  eventIds: string[];
  /** The main board's top three when the round started, if they are playing in it. */
  bounties: Bounty[];
  /** This round's twist, if it has one. */
  twist: Twist | null;
  /**
   * When a late score or void let the empty-hall hold go: the round was over, the changeover
   * done, nothing in, and then one came. The next round starts by itself one changeover after
   * this, so the wall can count down. Absent on a round that was never held, and in older logs.
   */
  releasedAt?: number;
}

export interface Clock {
  phase: Phase;
  /** The round the clock belongs to, while playing or in changeover. */
  roundNo: number | null;
  /** When the current phase ends, ms since the epoch. Null while idle. */
  phaseEndsAt: number | null;
  /** When the clock was paused (pause-all), else null. */
  pausedAt: number | null;
}

/**
 * A wind down that an admin has started (decision 42): the last rounds are drawn so everyone here
 * ends on `target` games. The sum is kept while it still adds up and worked out again when it
 * does not (someone arrives, leaves, takes a break, the courts change); see winddown.ts.
 */
export interface WindDown {
  /** When it was started, and by whom. */
  at: number;
  by: string;
  /** The admin took the near sum: up to three may end one game short when that saves rounds. */
  near: boolean;
  /** Everyone here ends on this many games... */
  target: number;
  /** ...except these, one game short. */
  short: PlayerId[];
  /** The sum when it was started, so the screens can say when it has changed. */
  first: { target: number; rounds: number; short?: PlayerId[] };
}

/** Where the owner has stepped the reveal to (decision 53). */
export interface RevealStep {
  step: number;
  of: number;
  places: number;
  at: number;
}

export interface TournamentState {
  config: Config;
  mode: Mode;
  players: Record<PlayerId, Player>;
  rounds: Round[];
  /** The next round's preview, if one has been drawn. */
  draft: Lineup | null;
  clock: Clock;
  pausedAll: boolean;
  /** When people are due back from the pause, if the admin said. Only a countdown: nothing resumes by itself. */
  pausedUntil: number | null;
  /** What the pause is, in a word or two ("Lunch"), if the admin said. */
  pauseLabel: string | null;
  /** `awards` on: the awards are handed out and their fun points count. */
  reveal: { main: boolean; fun: boolean; awards: boolean };
  /**
   * The reveal stepped from the owner's phone (decision 53): `step` 1 to `of` is award N on the
   * wall, then one place of the podium a step, 3rd first, `places` of them. `at` is when the step
   * was taken (the wall plays its music only for a fresh one). Null or absent: the old switches.
   */
  revealStep?: RevealStep | null;
  /** A message an admin put on the wall: shown for a short while from `at`. */
  notice: { text: string; at: number; by: string } | null;
  bench: Bench;
  /** The wind down, while one is on. Absent in a state made before decision 42: read it as off. */
  windDown?: WindDown | null;
  /**
   * The short talk before round 1 (0-based slide on the wall), or null when the slides are off.
   * Only the owner's phone moves them; a round starting ends them.
   */
  slides: number | null;
  /**
   * Mini games off for now (an admin's switch, the `minigames.set` event): no twist, no whole-game
   * mini game, nothing random. Absent in a state made before the switch existed: read it as on.
   */
  miniGamesOff?: boolean;
  /**
   * The whole-game mini game an admin set for the next round, kept while its preview is drawn
   * again (someone leaving, a break, Regenerate, a mode switch), so the new preview carries it.
   * Absent in a state made before it existed, and null or absent when none is set.
   */
  nextMiniGame?: string | null;
  /** Seed every draw derives from (seed + round number), so a replay draws the same line-ups. */
  seed: number;
  /** The last applied event's sequence number. */
  seq: number;
}

/** The event log entries. `at` is ms since the epoch; `by` is who did it (a role or a player). */
export type TournamentEvent =
  | { type: "tournament.created"; config: Config; players: { id: PlayerId; name: string }[]; seed: number }
  | { type: "config.updated"; patch: Partial<Omit<Config, "playersPerCourt">> }
  | { type: "player.added"; id: PlayerId; name: string; status?: PlayerStatus }
  | { type: "player.renamed"; id: PlayerId; name: string }
  | { type: "player.removed"; id: PlayerId }
  | { type: "player.status"; id: PlayerId; status: PlayerStatus }
  /** A player's own nickname; the picture version and the waiver are set by the server and admins only. */
  | { type: "player.profile"; id: PlayerId; nickname?: string | null; photo?: number; photoWaived?: boolean }
  /** A line for the wall: one of the written ones (`line`) or typed (`text`). */
  | { type: "heckle.sent"; player: PlayerId; court: number | null; line?: number; text?: string }
  | { type: "heckle.removed"; heckleSeq: number }
  | { type: "bench.muted"; on: boolean }
  /**
   * Curses on or off for the day (an admin's switch). Off ends every curse running and gives it
   * back to whoever threw it. Old logs never carry it: curses stay on.
   */
  | { type: "bench.curses"; on: boolean }
  /**
   * A curse on one pair, now. `kind` missing is wrong hand (every log before the choice). Solo's
   * stepping-off player is drawn by the reducer, never sent. (`eventId` is the old shape, a curse
   * on a court's next game: ignored.)
   */
  | { type: "curse.thrown"; player: PlayerId; court: number; side?: "a" | "b"; kind?: CurseKind; eventId?: string }
  | { type: "curse.lifted"; court: number }
  /** Who wins a court, in the round being played or the one shown as up next. */
  | { type: "pick.made"; player: PlayerId; roundNo: number; court: number; side: "a" | "b" }
  /** The old bets. Still in old logs; does nothing. */
  | { type: "bet.placed"; player: PlayerId; court: number; side: "a" | "b"; stake: number }
  /** Naming rights: a winner by a mile renames one of the losers for a couple of rounds. */
  | { type: "player.dubbed"; player: PlayerId; target: PlayerId; nickname: string }
  | { type: "player.undubbed"; id: PlayerId }
  | { type: "round.drafted"; lineup: Lineup | null }
  /** `twist`: a handicap that starts `afterMs` into the round and runs for the config's twistMs. */
  | { type: "round.started"; lineup: Lineup; eventIds?: string[]; twist?: { eventId: string; afterMs: number } }
  | { type: "round.finished" }
  | { type: "round.closed" }
  | { type: "clock.extended"; ms: number }
  /**
   * `startedAt`: when the round the score is for started, as the tablet saw it. A round taken back
   * and started again keeps its number but not its start, so a score queued for the old draw is
   * refused at the door. Missing (old logs, an admin's entry) is not checked.
   */
  | { type: "game.scored"; roundNo: number; court: number; score: Score; startedAt?: number }
  | { type: "game.corrected"; roundNo: number; court: number; score: Score; reason: string }
  | { type: "game.voided"; roundNo: number; court: number; reason: string }
  | { type: "mode.switched"; mode: Mode }
  /** A scoring mini game for a round, or (a handicap, every court, the round being played) a twist that starts now. */
  | { type: "event.fired"; roundNo: number; eventId: string; courts: number[] | "all" }
  | { type: "twist.stopped" }
  /** Take the scoring mini game off a round (the next one, or the one being played before any score). */
  | { type: "event.cleared"; roundNo: number }
  /** One player out of a line-up and another in: on the preview, or in a game with no score yet. */
  | { type: "lineup.swapped"; roundNo: number; out: PlayerId; in: PlayerId }
  /** A round started by mistake: taken back as if it never started. Only before any score is in. */
  | { type: "round.cancelled" }
  /** A message for the wall; null takes it down. */
  | { type: "wall.message"; text: string | null }
  /**
   * `until`: when people are due back, for the screens to count down to (nothing resumes by
   * itself). `label`: what the break is ("Lunch"). Sent again while paused they change those two
   * and nothing else; null takes one away.
   */
  | { type: "pause.all"; on: boolean; until?: number | null; label?: string | null }
  | { type: "reveal"; board: "main" | "fun" | "awards"; on: boolean }
  /**
   * The owner's Next or Back on the reveal (decision 53): `of` awards then `places` podium places
   * (as the owner's screen counts them), so the last step is `of + max(places, 1)`. Null takes the
   * reveal off the wall. Owner only.
   */
  | { type: "reveal.step"; step: number | null; of: number; places: number }
  /**
   * Wind down on or off (decision 42). `near`: the admin took the sum where up to three end one
   * game short, because it takes fewer rounds.
   */
  | { type: "winddown.set"; on: boolean; near?: boolean }
  /** The slides before round 1: which one is on the wall (0-based), or null for off. Owner only. */
  | { type: "slides.set"; slide: number | null }
  /**
   * Mini games on or off for the day (an admin's switch). Off ends a twist, takes the mini game
   * off the next round's preview, and stops the random ones. Old logs never carry it: mini games
   * stay on.
   */
  | { type: "minigames.set"; on: boolean };

export interface Envelope {
  seq: number;
  at: number;
  by: string;
}

export type LoggedEvent = TournamentEvent & Envelope;

export class EngineError extends Error {
  override name = "EngineError";
}
